import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { ArrowRight, Instagram } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackClick } from "@/hooks/useAnalytics";

interface Publication {
  id: string;
  title: string;
  tag: string | null;
  instagram_url: string | null;
  image_url: string | null;
  published_at: string | null;
  created_at: string;
}

export const Content = () => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, {
    once: true,
    margin: "-100px"
  });

  useEffect(() => {
    const fetchPublications = async () => {
      const { data, error } = await supabase
        .from("instagram_publications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(3);

      if (!error && data) {
        setPublications(data);
      }
      setLoading(false);
    };

    fetchPublications();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  return (
    <section id="conteudo" className="py-12 lg:py-16 bg-secondary/30" ref={containerRef}>
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 lg:mb-24"
        >
          <span className="text-primary text-sm font-medium tracking-widest uppercase mb-4 block">
            Redes Sociais
          </span>
          <h2 className="font-display text-4xl lg:text-6xl xl:text-7xl font-light text-foreground mb-4">
            <span className="text-primary">Publicações</span>
          </h2>
          <p className="text-muted-foreground max-w-xl">
            Acompanhe nossas últimas publicações no Instagram e outras redes sociais.
          </p>
        </motion.div>

        {/* Posts Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card border border-border rounded-sm animate-pulse">
                <div className="aspect-square bg-muted" />
                <div className="p-6 space-y-3">
                  <div className="h-4 bg-muted rounded w-1/3" />
                  <div className="h-6 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : publications.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Nenhuma publicação disponível no momento.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {publications.map((publication, index) => (
              <motion.article
                key={publication.id}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  delay: index * 0.15,
                  duration: 0.8,
                  ease: [0.22, 1, 0.36, 1]
                }}
                className="group cursor-pointer"
              >
                <a
                  href={publication.instagram_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  onClick={() => trackClick("Publication", publication.title)}
                >
                  <div className="relative bg-card border border-border rounded-sm overflow-hidden h-full hover:border-primary/50 transition-all duration-500 card-hover">
                    {/* Image */}
                    <div className="aspect-square relative overflow-hidden">
                      <motion.img
                        src={publication.image_url || "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400&h=400&fit=crop&auto=format"}
                        alt={publication.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {publication.tag && (
                        <span className="absolute top-4 left-4 text-xs font-medium tracking-wider uppercase bg-primary/90 text-primary-foreground px-3 py-1 rounded-sm">
                          {publication.tag}
                        </span>
                      )}
                      <div className="absolute top-4 right-4">
                        <Instagram className="w-5 h-5 text-foreground/80" aria-hidden="true" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {/* Meta */}
                      <div className="flex items-center gap-4 text-muted-foreground text-xs mb-3">
                        <span>{formatDate(publication.published_at || publication.created_at)}</span>
                      </div>

                      {/* Title */}
                      <h3 className="font-display text-lg lg:text-xl text-foreground mb-3 group-hover:text-primary transition-colors duration-300 leading-tight">
                        {publication.title}
                      </h3>

                      {/* Read More */}
                      <motion.span
                        className="inline-flex items-center gap-2 text-primary text-sm font-medium"
                        whileHover={{ x: 5 }}
                      >
                        Ver no Instagram
                        <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </motion.span>
                    </div>
                  </div>
                </a>
              </motion.article>
            ))}
          </div>
        )}

        {/* View All */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center"
        >
          <motion.a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-2 px-8 py-4 border border-primary text-primary font-medium hover:bg-primary hover:text-primary-foreground transition-all duration-300 rounded-sm"
          >
            <Instagram className="w-5 h-5" />
            Seguir no Instagram
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};
